import Phaser from 'phaser';
import { BASE_H, BASE_W, COLORS, MAX_LIVES, TILE_SIZE } from '@/config/constants';
import { configFor } from '@/services/difficultyCurve';
import { generateOperation } from '@/services/operationGenerator';
import { generateDotLayout } from '@/services/dotLayoutGenerator';
import { audioService } from '@/services/audioService';
import { useGameStore } from '@/state/gameStore';
import { spawnRound } from '@/game/systems/TileSpawner';
import { scoreForHit } from '@/game/systems/ScoreSystem';
import { isOffscreen, spawnPositionFor } from '@/game/systems/MotionController';
import { TileSprite } from '@/game/objects/TileSprite';
import { Comet } from '@/game/objects/Comet';
import { HUD } from '@/game/objects/HUD';
import type { DifficultyConfig, MathOperation, TileSpec } from '@/types/models';

const ROUND_TRANSITION_MS = 450;

// Pasos del tutorial: 2 operaciones sencillas con 3 tiles cada una.
const TUTORIAL_STEPS: Array<{ a: number; b: number; result: number; counts: number[] }> = [
  { a: 1, b: 1, result: 2, counts: [1, 2, 3] },
  { a: 1, b: 2, result: 3, counts: [2, 3, 4] }
];

export class GameScene extends Phaser.Scene {
  private hud!: HUD;
  private tiles: TileSprite[] = [];
  private currentOp: MathOperation | null = null;
  private cfg!: DifficultyConfig;
  private roundStartedAt = 0;
  private roundDurationMs = 8000;
  private spawnIntervalMs = 800;
  private timeUntilNextSpawn = 0;
  private spawnQueue: number[] = [];
  private gameOverFlag = false;
  private roundClosed = false;
  private pendingTilesSpec: ReturnType<typeof spawnRound> = [];

  private comet: Comet | null = null;
  private cometPrev: { x: number; y: number } | null = null;
  private activePointerId: number | null = null;

  /** Demo del menú: no afecta lives/score/level del store y nunca dispara game over. */
  private demoMode = false;
  private static readonly DEMO_LEVEL = 4; // Nivel 5 visual: parabólico, vistoso

  /** Tutorial: tiles estáticos, 2 operaciones simples, emoji 👇 sobre el correcto. */
  private tutorialMode = false;
  private tutorialStepIdx = 0;
  private tutorialPointer: Phaser.GameObjects.Text | null = null;
  private tutorialHomeBtn: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('Game');
  }

  create(): void {
    this.gameOverFlag = false;
    this.roundClosed = false;
    this.comet = null;
    this.cometPrev = null;
    this.activePointerId = null;
    this.tiles = [];
    this.spawnQueue = [];
    this.pendingTilesSpec = [];
    const screen = useGameStore.getState().screen;
    this.tutorialMode = screen === 'tutorial';
    this.demoMode = !this.tutorialMode && screen !== 'game';
    this.tutorialStepIdx = 0;
    this.tutorialPointer = null;
    this.tutorialHomeBtn = null;

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bg, 1);
    bg.fillRect(0, 0, BASE_W, BASE_H);
    bg.setDepth(-10);

    this.hud = new HUD(this);
    this.hud.setDepth(0);
    this.hud.setTutorialMode(this.tutorialMode);

    if (this.tutorialMode) {
      this.tutorialHomeBtn = this.add
        .text(80, 130, '🏠', { fontFamily: 'Inter, system-ui', fontSize: '64px' })
        .setOrigin(0, 0.5)
        .setDepth(150);
      this.tutorialHomeBtn.setInteractive({ useHandCursor: true });
      this.tutorialHomeBtn.on(
        'pointerdown',
        (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          useGameStore.getState().reset();
        }
      );
    }

    this.setupCometInput();

    audioService.enableMusic();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      audioService.disableMusic();
      this.teardownComet();
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      audioService.disableMusic();
      this.teardownComet();
    });

    this.startRound();
  }

  private setupCometInput(): void {
    this.input.addPointer(2); // soporte multi-touch básico (solo usamos 1 a la vez)

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.gameOverFlag) return;
      if (this.activePointerId !== null) return; // ya hay un dedo activo
      // No spawnear cometa si el tap cae sobre un control del HUD (ej. bocina o casa).
      if (this.hud.isPointerOverInteractive(p.worldX, p.worldY)) return;
      if (
        this.tutorialHomeBtn &&
        this.tutorialHomeBtn.getBounds().contains(p.worldX, p.worldY)
      ) {
        return;
      }
      this.activePointerId = p.id;
      this.spawnComet(p.worldX, p.worldY);
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.comet || !p.isDown) return;
      if (this.activePointerId !== null && p.id !== this.activePointerId) return;
      this.comet.follow(p.worldX, p.worldY);
    });

    const releaseHandler = (p: Phaser.Input.Pointer) => {
      if (this.activePointerId !== null && p.id !== this.activePointerId) return;
      this.dismissComet();
    };
    this.input.on('pointerup', releaseHandler);
    this.input.on('pointerupoutside', releaseHandler);
    this.input.on('pointercancel', releaseHandler);
  }

  private spawnComet(x: number, y: number): void {
    if (this.comet) {
      this.comet.destroy();
      this.comet = null;
    }
    this.comet = new Comet(this, x, y);
    this.cometPrev = { x, y };
  }

  private dismissComet(): void {
    if (this.comet) {
      this.comet.fadeOut();
      this.comet = null;
    }
    this.cometPrev = null;
    this.activePointerId = null;
  }

  private teardownComet(): void {
    if (this.comet) {
      this.comet.destroy();
      this.comet = null;
    }
    this.cometPrev = null;
    this.activePointerId = null;
  }

  private startRound(): void {
    this.roundClosed = false;

    if (this.tutorialMode) {
      this.startTutorialStep(this.tutorialStepIdx);
      return;
    }

    if (this.demoMode) {
      this.cfg = configFor(GameScene.DEMO_LEVEL);
      this.currentOp = generateOperation(this.cfg);
      this.hud.setOperation(this.currentOp);
      this.hud.setLevel(GameScene.DEMO_LEVEL);
      this.hud.setScore(0);
      this.hud.setLives(MAX_LIVES);
      this.hud.setCombo(0);
      this.hud.setTimer(1);
    } else {
      const state = useGameStore.getState();
      this.cfg = configFor(state.level);
      this.currentOp = generateOperation(this.cfg);
      state.setOperation(this.currentOp);

      this.hud.setOperation(this.currentOp);
      this.hud.setLevel(state.level);
      this.hud.setScore(state.score);
      this.hud.setLives(state.lives);
      this.hud.setCombo(state.combo);
      this.hud.setTimer(1);
    }

    const tilesSpec = spawnRound(this.currentOp, this.cfg);
    this.spawnQueue = Array(tilesSpec.length).fill(0).map((_, i) => i);
    this.pendingTilesSpec = tilesSpec;

    this.roundStartedAt = this.time.now;
    this.roundDurationMs = this.cfg.roundDurationSec * 1000;
    // Mínimo 800ms entre spawns para que no se lancen muchos al mismo tiempo.
    this.spawnIntervalMs = Math.max(800, this.roundDurationMs / (tilesSpec.length + 1));
    this.timeUntilNextSpawn = 0;
  }

  update(_time: number, delta: number): void {
    if (this.gameOverFlag) return;

    if (!this.roundClosed) {
      this.timeUntilNextSpawn -= delta;
      while (this.timeUntilNextSpawn <= 0 && this.spawnQueue.length > 0) {
        const idx = this.spawnQueue.shift()!;
        const spec = this.pendingTilesSpec[idx];
        if (spec) {
          this.spawnTile(spec);
        }
        this.timeUntilNextSpawn += this.spawnIntervalMs;
      }
    }

    for (let i = this.tiles.length - 1; i >= 0; i--) {
      const tile = this.tiles[i];
      if (!tile.active || !tile.scene) {
        this.tiles.splice(i, 1);
        continue;
      }
      tile.applyMotion(delta);
      if (isOffscreen(tile)) {
        if (!this.roundClosed && tile.spec.isCorrect) {
          this.handleMissedCorrect();
          tile.destroy();
          this.tiles.splice(i, 1);
          return;
        }
        tile.destroy();
        this.tiles.splice(i, 1);
      }
    }

    if (this.comet) {
      this.comet.tick();
      this.checkCometCollisions();
    }

    if (!this.tutorialMode) {
      const elapsed = this.time.now - this.roundStartedAt;
      const remaining = Math.max(0, this.roundDurationMs - elapsed);
      this.hud.setTimer(remaining / this.roundDurationMs);

      if (!this.roundClosed && remaining <= 0 && this.spawnQueue.length === 0) {
        const stillHasCorrect = this.tiles.some((t) => t.spec.isCorrect);
        if (!stillHasCorrect) {
          this.handleRoundTimeout();
        }
      }
    }

    if (
      !this.demoMode &&
      !this.tutorialMode &&
      useGameStore.getState().lives <= 0 &&
      !this.gameOverFlag
    ) {
      this.endGame();
    }
  }

  private checkCometCollisions(): void {
    if (!this.comet || this.roundClosed) return;
    const cx = this.comet.x;
    const cy = this.comet.y;
    const r = this.comet.collisionRadius;

    const prev = this.cometPrev ?? { x: cx, y: cy };
    const dx = cx - prev.x;
    const dy = cy - prev.y;
    const dist = Math.hypot(dx, dy);
    // Sub-pasos para evitar tunneling en swipes rápidos. Paso máximo = 80% del radio.
    const stepLen = r * 0.8;
    const steps = Math.max(1, Math.ceil(dist / stepLen));

    for (const tile of this.tiles) {
      if (!tile.active || tile.consumed) continue;
      let hit = false;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const sx = prev.x + dx * t;
        const sy = prev.y + dy * t;
        if (tile.intersectsCircle(sx, sy, r)) {
          hit = true;
          break;
        }
      }
      if (hit) {
        this.handleCometHit(tile);
        break;
      }
    }

    this.cometPrev = { x: cx, y: cy };
  }

  private startTutorialStep(idx: number): void {
    this.tutorialStepIdx = idx;

    if (idx >= TUTORIAL_STEPS.length) {
      // Tutorial completo → transición al juego real.
      this.time.delayedCall(300, () => {
        useGameStore.getState().startGame();
      });
      return;
    }

    this.roundClosed = false;
    const step = TUTORIAL_STEPS[idx];
    this.currentOp = { a: step.a, b: step.b, op: '+', result: step.result };
    this.hud.setOperation(this.currentOp);
    this.hud.setTimer(1);

    // Construir 3 specs con dotCounts fijos.
    const palette = COLORS.tilePalette;
    const specs: TileSpec[] = step.counts.map((count, i) => ({
      id: `tut-${idx}-${i}`,
      dotCount: count,
      dotPositions: generateDotLayout({ count }),
      color: palette[(idx * 3 + i) % palette.length],
      isCorrect: count === step.result
    }));
    // Mezclar para que el correcto no quede siempre en la misma posición.
    for (let i = specs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [specs[i], specs[j]] = [specs[j], specs[i]];
    }

    const yPos = BASE_H * 0.62;
    const xSpread = TILE_SIZE * 1.3;
    const positions = [
      { x: BASE_W / 2 - xSpread, y: yPos },
      { x: BASE_W / 2, y: yPos },
      { x: BASE_W / 2 + xSpread, y: yPos }
    ];

    specs.forEach((spec, i) => {
      const pos = positions[i];
      const tile = new TileSprite(this, spec, pos.x, pos.y);
      tile.vx = 0;
      tile.vy = 0;
      tile.gravity = 0;
      tile.gravityDown = null;
      tile.terminalVy = Infinity;
      this.tiles.push(tile);
    });

    const correct = this.tiles.find((t) => t.spec.isCorrect);
    if (correct) this.spawnTutorialPointer(correct.x, correct.y);
  }

  private spawnTutorialPointer(tileX: number, tileY: number): void {
    this.clearTutorialPointer();
    const baseY = tileY - TILE_SIZE / 2 - 80;
    this.tutorialPointer = this.add
      .text(tileX, baseY, '👇', { fontFamily: 'Inter, system-ui', fontSize: '110px' })
      .setOrigin(0.5)
      .setDepth(150);
    this.tweens.add({
      targets: this.tutorialPointer,
      y: baseY - 35,
      duration: 450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut'
    });
  }

  private clearTutorialPointer(): void {
    if (this.tutorialPointer) {
      this.tweens.killTweensOf(this.tutorialPointer);
      this.tutorialPointer.destroy();
      this.tutorialPointer = null;
    }
  }

  private spawnTile(spec: ReturnType<typeof spawnRound>[number]): void {
    const motion = spawnPositionFor(this.cfg.motion, this.cfg.level);
    const tile = new TileSprite(this, spec, motion.x, motion.y);
    tile.vx = motion.vx;
    tile.vy = motion.vy;
    tile.gravity = motion.gravity;
    tile.gravityDown = motion.gravityDown ?? null;
    tile.terminalVy = motion.terminalVy ?? Infinity;
    this.tiles.push(tile);
  }

  private handleCometHit(tile: TileSprite): void {
    if (this.gameOverFlag || this.roundClosed) return;

    if (this.tutorialMode) {
      if (tile.spec.isCorrect) {
        this.roundClosed = true;
        audioService.play('correct');
        tile.flashCorrect();
        this.clearTutorialPointer();
        this.fadeOutOthers(tile);
        this.time.delayedCall(ROUND_TRANSITION_MS, () => {
          this.startTutorialStep(this.tutorialStepIdx + 1);
        });
      } else {
        // Wrong en tutorial: feedback, sin cerrar el round; el usuario puede reintentar.
        audioService.play('wrong');
        this.cameras.main.shake(120, 0.005);
        this.tweens.add({
          targets: tile,
          angle: { from: -8, to: 8 },
          duration: 60,
          yoyo: true,
          repeat: 2
        });
        // Cooldown breve para evitar disparos múltiples sobre el mismo tap.
        this.roundClosed = true;
        this.time.delayedCall(500, () => {
          this.roundClosed = false;
        });
      }
      return;
    }

    this.roundClosed = true;

    if (this.demoMode) {
      if (tile.spec.isCorrect) {
        audioService.play('correct');
        tile.flashCorrect();
      } else {
        audioService.play('wrong');
        tile.flashWrong();
      }
      this.fadeOutOthers(tile);
      this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
      return;
    }

    const state = useGameStore.getState();

    if (tile.spec.isCorrect) {
      const elapsed = this.time.now - this.roundStartedAt;
      const remaining = Math.max(0, this.roundDurationMs - elapsed);
      const points = scoreForHit(state.combo, remaining, this.roundDurationMs);
      state.registerHit(points);
      audioService.play('correct');
      tile.flashCorrect();
      this.fadeOutOthers(tile);
      state.nextLevel();
      this.hud.setScore(useGameStore.getState().score);
      this.hud.setCombo(useGameStore.getState().combo);
      this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
    } else {
      state.registerMiss();
      audioService.play('wrong');
      tile.flashWrong();
      this.cameras.main.shake(180, 0.005);
      this.hud.setLives(useGameStore.getState().lives);
      this.hud.setCombo(0);
      if (useGameStore.getState().lives <= 0) {
        this.endGame();
        return;
      }
      this.fadeOutOthers(tile);
      this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
    }
  }

  private handleMissedCorrect(): void {
    if (this.roundClosed) return;
    this.roundClosed = true;

    if (this.demoMode) {
      this.fadeOutOthers(null);
      this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
      return;
    }

    const state = useGameStore.getState();
    state.registerMiss();
    audioService.play('wrong');
    this.cameras.main.shake(120, 0.003);
    this.hud.setLives(useGameStore.getState().lives);
    this.hud.setCombo(0);
    if (useGameStore.getState().lives <= 0) {
      this.endGame();
      return;
    }
    this.fadeOutOthers(null);
    this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
  }

  private handleRoundTimeout(): void {
    if (this.roundClosed) return;
    this.roundClosed = true;

    if (this.demoMode) {
      this.fadeOutOthers(null);
      this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
      return;
    }

    const state = useGameStore.getState();
    if (state.lives <= 0) {
      this.endGame();
      return;
    }
    this.fadeOutOthers(null);
    this.time.delayedCall(ROUND_TRANSITION_MS, () => this.startRound());
  }

  private fadeOutOthers(except: TileSprite | null): void {
    this.tiles.forEach((t) => {
      if (t === except || !t.active) return;
      this.tweens.add({
        targets: t,
        alpha: 0,
        duration: 200,
        onComplete: () => t.destroy()
      });
    });
    this.tiles = except ? [except] : [];
    this.spawnQueue = [];
    this.pendingTilesSpec = [];
  }

  private endGame(): void {
    if (this.gameOverFlag) return;
    this.gameOverFlag = true;
    this.roundClosed = true;
    useGameStore.getState().endGame();
    this.tiles.forEach((t) => t.destroy());
    this.tiles = [];
    this.teardownComet();
    this.scene.stop();
  }
}
